import { pickText, type Locale } from "./index.js";

export type LocalizedPlain = Record<string, string>;

/** Prefix so plain demo/seed posts stay readable as-is. */
export const FEED_LOCALIZED_PREFIX = "\u001eHLFEED:";

function combinedBlock(title: LocalizedPlain, body: LocalizedPlain, loc: Locale): string {
  const t = (title[loc] ?? title.de ?? "").trim();
  const b = (body[loc] ?? body.de ?? "").trim();
  if (t && b) return `${t}\n\n${b}`;
  return t || b;
}

/** Store one feed row; JSON payload when English differs from German. */
export function encodeFeedPostBody(title: LocalizedPlain, body: LocalizedPlain): string {
  const de = combinedBlock(title, body, "de");
  const hasEn = Boolean(title.en?.trim() || body.en?.trim());
  const en = hasEn ? combinedBlock(title, body, "en") : "";
  if (en && en !== de) return `${FEED_LOCALIZED_PREFIX}${JSON.stringify({ de, en })}`;
  return de;
}

/** Resolve feed body for the viewer's locale (plain text or prefixed JSON). */
export function decodeFeedPostBody(stored: string, locale: Locale): string {
  if (!stored.startsWith(FEED_LOCALIZED_PREFIX)) return stored;
  try {
    const o = JSON.parse(stored.slice(FEED_LOCALIZED_PREFIX.length)) as LocalizedPlain;
    return pickText(o, locale);
  } catch {
    return stored;
  }
}

/** Parse title/body columns that may be plain text or `{"de","en"}`. */
export function parseStoredLocalized(stored: string): LocalizedPlain {
  if (stored.startsWith(FEED_LOCALIZED_PREFIX)) {
    try {
      return JSON.parse(stored.slice(FEED_LOCALIZED_PREFIX.length)) as LocalizedPlain;
    } catch {
      return { de: stored, en: stored };
    }
  }
  if (stored.startsWith("{")) {
    try {
      const o = JSON.parse(stored) as LocalizedPlain;
      if (typeof o.de === "string") return o;
    } catch {
      /* plain */
    }
  }
  return { de: stored, en: stored };
}

/** Persist localized send title/body on audience_sends rows. */
export function encodeStoredLocalized(value: LocalizedPlain): string {
  const de = (value.de ?? "").trim();
  const en = (value.en ?? "").trim();
  if (en && en !== de) return JSON.stringify({ de, en });
  return de;
}
